<template>
  <div class="left-pane">
    <BaseResizableLeftPane
      :is-resizable="false"
      storage-key="settings/menu:width"
      @resized="(w) => $emit('isSidebarResized', w)"
    >
      <div class="resizable-content">
        <BasePaneHeader :is-hideable="false" :compact="compact" />

        <div class="menu">
          <!-- Organizations Section -->
          <Organizations />

          <div class="menu-section">
            <div class="title">Account</div>
            <SettingsMenuItem
              :icon="PersonIcon"
              label="Account"
              :selected="isRoute(RouteNames.SettingsAccount)"
              @click="() => navigate(RouteNames.SettingsAccount)"
            />
            <SettingsMenuItem
              :icon="PreferencesIcon"
              label="Preferences"
              :selected="isRoute(RouteNames.SettingsPreferences)"
              @click="() => navigate(RouteNames.SettingsPreferences)"
            />
            <SettingsMenuItem
              :icon="CalendarsFavoriteIcon"
              label="Calendars"
              :selected="isRoute(RouteNames.SettingsCalendars)"
              @click="() => navigate(RouteNames.SettingsCalendars)"
            />
            <SettingsMenuItem
              :icon="HandshakeIcon"
              label="Meetings"
              :selected="isRoute(RouteNames.SettingsMeetings)"
              @click="() => navigate(RouteNames.SettingsMeetings)"
            />
            <SettingsMenuItem
              :icon="DashboardIcon"
              label="Dashboard"
              :selected="isRoute(RouteNames.SettingsDashboard)"
              @click="() => navigate(RouteNames.SettingsDashboard)"
            />
            <SettingsMenuItem
              :icon="LockIcon"
              label="Permissions"
              :selected="isRoute(RouteNames.SettingsPermissions)"
              @click="() => navigate(RouteNames.SettingsPermissions)"
            />
            <SettingsMenuItem
              :icon="MenuIcon"
              label="Menu"
              :selected="isRoute(RouteNames.SettingsMenu)"
              entitlement-key="customizable-menu"
              @click="() => navigate(RouteNames.SettingsMenu)"
            />
            <SettingsMenuItem
              :icon="PluginIcon"
              label="Plugins"
              :selected="isRoute(RouteNames.SettingsPlugins)"
              @click="() => navigate(RouteNames.SettingsPlugins)"
            />
            <SettingsMenuItem
              class="apps-menu-item"
              :icon="AppsIcon"
              label="Apps"
              :selected="isRoute(RouteNames.SettingsApps)"
              @click="() => navigate(RouteNames.SettingsApps)"
            />
            <SettingsMenuItem
              :icon="InviteIcon"
              label="Invite people"
              :selected="isRoute(RouteNames.SettingsInvite)"
              @click="() => navigate(RouteNames.SettingsInvite)"
            />
            <SettingsMenuItem
              :icon="CodeIcon"
              label="Logs"
              :selected="isRoute(RouteNames.SettingsLogs)"
              @click="() => navigate(RouteNames.SettingsLogs)"
            />
            <SettingsMenuItem
              v-if="isBetaTester"
              :icon="BindingIcon"
              label="Task Bindings"
              :selected="isRoute(RouteNames.SettingsTaskBindings)"
              @click="() => navigate(RouteNames.SettingsTaskBindings)"
            />
          </div>
        </div>
      </div>
    </BaseResizableLeftPane>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useRoute } from "vue-router";

import BasePaneHeader from "@/components/base/BasePaneHeader.vue";
import BaseResizableLeftPane from "@/components/base/BaseResizableLeftPane.vue";
import AppsIcon from "@/components/icons/AppsIcon.vue";
import BindingIcon from "@/components/icons/BindingIcon.vue";
import CalendarsFavoriteIcon from "@/components/icons/CalendarsFavoriteIcon.vue";
import CodeIcon from "@/components/icons/CodeIcon.vue";
import DashboardIcon from "@/components/icons/DashboardIcon.vue";
import EventIcon from "@/components/icons/EventIcon.vue";
import HandshakeIcon from "@/components/icons/HandshakeIcon.vue";
import InviteIcon from "@/components/icons/InviteIcon.vue";
import LockIcon from "@/components/icons/LockIcon.vue";
import MenuIcon from "@/components/icons/MenuIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import PluginIcon from "@/components/icons/PluginIcon.vue";
import PreferencesIcon from "@/components/icons/PreferencesIcon.vue";
import TableIcon from "@/components/icons/TableIcon.vue";
import SettingsIcon from "@/components/icons/tabs/SettingsIcon.vue";
import * as inject from "@/components/inject";
import SettingsMenuItem from "@/components/menu/MenuItemSettings.vue";
import Organizations from "@/components/settings/Organizations.vue";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

export default defineComponent({
  components: {
    BasePaneHeader,
    BaseResizableLeftPane,
    Organizations,
    SettingsMenuItem,
  },
  props: {
    compact: { type: Boolean, default: false },
  },
  emits: {
    isSidebarResized: (_width: number) => true,
  },
  async setup() {
    const router = inject.router();
    const isBetaTester = inject.isBetaTester();
    const route = useRoute();

    const isRoute = (name: RouteNames) => route?.name === name;
    const navigate = (destination: RouteNames) =>
      navigateTo(destination, router);

    return {
      isBetaTester,
      isRoute,
      navigate,
      RouteNames,
      AppsIcon,
      CalendarsFavoriteIcon,
      BindingIcon,
      DashboardIcon,
      HandshakeIcon,
      PreferencesIcon,
      LockIcon,
      EventIcon,
      SettingsIcon,
      MenuIcon,
      PersonIcon,
      PluginIcon,
      InviteIcon,
      CodeIcon,
      TableIcon,
    };
  },
});
</script>

<style lang="scss" scoped>
.left-pane {
  .resizable-content {
    width: 100%;

    .menu {
      overflow-x: hidden;
      height: calc(100% - 58px);
      padding-bottom: 12px;

      .menu-section {
        display: flex;
        flex-direction: column;
        row-gap: 2px;
        padding: 12px 12px 24px 12px;

        // AppsIcon's artwork fills its viewBox edge-to-edge, unlike the other
        // menu icons (24px viewBoxes with built-in padding), so at the menu's
        // 18px it looks oversized. Render it smaller, but keep the 18px slot
        // so the label stays aligned with its siblings.
        .apps-menu-item :deep(svg) {
          width: 14px;
          margin-inline: 2px;
        }

        .title {
          margin-bottom: 4px;
          padding-inline-start: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          @extend %font-h5;

          @include themed() {
            color: t($grey-50);
          }
        }
      }
    }
  }
}
</style>
